import React from "react"
import moment from "moment-timezone"
import { isSortingEvent } from "./EventStage"
import { EventSportTag } from "./EventSport"

export const eventStatus = status => ({
  ACCEPTED: "Active", CONFIRMED: "Active", DISABLED: "Disabled", CREATED: "Active",
  PENDING: "Active",
})[status] || (status ? "Unknown (" + status + ")" : "Status unavailable")
export const eventStatusClass = status => eventStatus(status) === "Active" ? "active" : status === "DISABLED" ? "disabled" : "unknown"
export function eventClassification(event, settings = {}) {
  return [[event.sportType, settings.activeSports], [event.category, settings.categories], [event.playMode, settings.playModes]]
    .filter(([value]) => value)
    .map(([value, options]) => {
      const option = (options || []).find(item => item?.key === value)
      const label = String(option?.name || value)
      return label.charAt(0).toUpperCase() + label.slice(1)
    }).join(" · ") || "Classification unavailable"
}
// The event's sport tag and category badge, side by side above its name (the canvas verdict B, 2026-10-29).
export function EventClassification({ event, settings = {} }) {
  const category = event.category && eventClassification({ category: event.category }, settings)
  const tone = ["gold", "silver", "platinum"].includes(event.category) ? event.category : "default"
  return <span className="event-badges">
    <EventSportTag event={event} />
    {category && <span className={"badge event-category event-category-" + tone} aria-label={"Category: " + category}>{category}</span>}
  </span>
}
// „#39031 · Weekend”: the event's number, then its format.
export function EventReference({ event, settings = {} }) {
  const format = event.playMode && eventClassification({ playMode: event.playMode }, settings)
  return <small>#{event.eventId ?? "—"}{format ? " · " + format : ""}</small>
}
export const formatEventDate = (value, timezone) => {
  if (!value || !moment(value, moment.ISO_8601, true).isValid()) return "Date unavailable"
  return moment(value).tz(moment.tz.zone(timezone) ? timezone : "UTC").format("DD MMM YYYY, HH:mm")
}
export function EventSchedule({ event }) {
  const valid = event.startDate && event.endDate && moment(event.startDate).isValid() && moment(event.endDate).isValid()
    && moment(event.endDate).valueOf() >= moment(event.startDate).valueOf()
  const timezone = moment.tz.zone(event.timezone) ? event.timezone : "UTC"
  const preRegistrationStart = isSortingEvent(event) && event.preRegistrationPhase?.startDate
  return <div className="event-stack">
    {valid ? <><span>{formatEventDate(event.startDate, timezone)}</span><span>to {formatEventDate(event.endDate, timezone)}</span></>
      : <span className="text-danger">Schedule unavailable or invalid</span>}
    <small>{timezone}{timezone !== event.timezone ? " (event timezone unavailable)" : ""}</small>
    {preRegistrationStart && <small>Pre-registration opens {formatEventDate(preRegistrationStart, timezone)}</small>}
    {event.registrationPhase?.startDate && <small>Registration opens {formatEventDate(event.registrationPhase.startDate, timezone)}</small>}
    {event.registrationPhase?.endDate && <small>Registration closes {formatEventDate(event.registrationPhase.endDate, timezone)}</small>}
  </div>
}
const progressStates = ["none", "partial", "partialWithoutFinal", "complete"]
// Disabled competitions stay listed, but they never count towards the results of an event.
export const activeCompetitions = competitions => (competitions || []).filter(c => c && c.status !== "DISABLED")
export const resultsSummary = (competitions, field) => {
  const active = activeCompetitions(competitions)
  return { total: active.length, complete: active.filter(c => c[field] === "complete").length,
    known: active.filter(c => progressStates.includes(c[field])).length, disabled: (competitions || []).length - active.length }
}
function Progress({ label, field, event }) {
  const start = event.playingPhase?.startDate
  const { total, complete, known, disabled } = resultsSummary(event.competitions, field)
  let state = "neutral", message = label + ": unavailable", detail
  if (event.competitionsAvailable !== false && total === 0) message = label + (disabled > 0 ? ": all competitions disabled" : ": no competitions")
  else if (event.competitionsAvailable !== false && start && moment(start).isValid()) {
    if (moment(start).valueOf() > Date.now()) message = label + ": not started"
    else {
      detail = complete + " / " + total + " complete" + (disabled > 0 ? " · " + disabled + " disabled excluded" : "")
      if (known < total) detail += " · some unavailable"
      else {
        state = complete === total ? "complete" : "incomplete"
        message = label + " " + state
      }
    }
  }
  return <div className="event-progress">
    <span className={"event-progress-badge event-progress-" + state}>
      <span aria-hidden="true">{state === "complete" ? "✓" : state === "incomplete" ? "!" : "—"}</span>
      <span>{message}</span>
    </span>
    {detail && <small className="text-muted">{detail}</small>}
  </div>
}
export function EventProgress({ event }) {
  const maintenance = (event.competitions || []).filter(c => c.status === "MAINTENANCE").length
  return <div className="event-stack"><strong className={"badge event-status-badge event-status-badge-" + eventStatusClass(event.status)}>{eventStatus(event.status)}</strong>
    {maintenance > 0 && <small>Maintenance: {maintenance} competition{maintenance === 1 ? "" : "s"}</small>}
    <Progress label="Scores" field="scoresCompletion" event={event} />
    <Progress label="Points" field="pointsSaved" event={event} />
  </div>
}
export function ResultsSummary({ competitions }) {
  const scores = resultsSummary(competitions, "scoresCompletion"), points = resultsSummary(competitions, "pointsSaved")
  const text = summary => summary.complete + " / " + summary.total + " complete" + (summary.known < summary.total ? " (some unavailable)" : "")
  return <p className="results-summary">
    {scores.total === 0 ? "Results: no active competitions" : "Scores " + text(scores) + " · Points " + text(points)}
    {scores.disabled > 0 && <small className="text-muted"> · {scores.disabled} disabled excluded</small>}
  </p>
}
const drawWords = { luckyDraw: ["Lucky Draw", "run", "not run"], mainDraw: ["Main Draw", "generated", "not generated"] }
// Draw evidence arrives with each listing. An unknown value means the match store could not be read, not that the draw is missing.
export function DrawStatus({ competition, compact = false, stacked = false }) {
  return <span className={"draw-status" + (compact ? " draw-status-compact" : "") + (stacked ? " draw-status-stacked" : "")}>
    {Object.entries(drawWords).map(([key, [label, done, pending]]) => {
      const value = competition.drawStatus?.[key]
      const state = value === true ? "done" : value === false ? "pending" : "unknown"
      const text = state === "done" ? done : state === "pending" ? pending : "unavailable"
      return <span key={key} className={"draw-badge draw-badge-" + state} title={label + ": " + text}>
        <span aria-hidden="true">{state === "done" ? "✓" : state === "pending" ? "○" : "?"}</span>
        <span className="draw-status-label">{label}</span><span className={compact ? "sr-only" : "draw-status-value"}>{stacked ? text : ": " + text}</span>
      </span>
    })}
  </span>
}
